import { useAuth } from "@clerk/clerk-react";
import { convexQuery } from "@convex-dev/react-query";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import { ConvexHttpClient } from "convex/browser";
import { useMutation } from "convex/react";
import { ChevronLeft, Heart, MapPin, Pencil, User } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { api } from "#/../convex/_generated/api";
import type { Doc, Id } from "#/../convex/_generated/dataModel";
import { Disc3D } from "#/components/disc/Disc3D";
import {
	InstallBanner,
	InstallBannerDesktop,
	IOSTipSheet,
} from "#/components/install-prompt";
import { Badge } from "#/components/ui/badge";
import { Button } from "#/components/ui/button";
import { Skeleton } from "#/components/ui/skeleton";
import {
	hasReachedViewThreshold,
	incrementListingViewCount,
	useInstallPrompt,
} from "#/hooks/useInstallPrompt";
import { useSignInModal } from "#/hooks/useSignInModal";

const LISTING_DISC_SIZE = 460;

import { ContactSellerPanel } from "#/components/listing/ContactSellerPanel";
import { FlightNumbersGrid } from "#/components/listing/FlightNumbersGrid";
import { ImageGallery } from "#/components/listing/ImageGallery";
import { ListingNotFoundPage } from "#/components/listing/ListingNotFoundPage";
import { ReportListingButton } from "#/components/listing/ReportListingButton";
import { SoldListingPage } from "#/components/listing/SoldListingPage";
import {
	COLOR_HEX,
	CONDITION_LABEL,
	DISC_CLASS_LABEL,
	DYED_GRADIENT,
	TRANSPARENT_PATTERN,
} from "#/lib/constants";
import { cn } from "#/lib/utils";

function buildListingMeta(
	listing: Awaited<ReturnType<typeof fetchListing>> | undefined,
) {
	if (!listing || listing.soldAt !== undefined) {
		return {
			title: "Disc Listing | Re:Disc",
			description: "View this disc golf disc listing on Re:Disc.",
		};
	}
	const parts: string[] = [];
	if (listing.manufacturerName) parts.push(listing.manufacturerName);
	if (listing.discModelName) parts.push(listing.discModelName);
	const discName = parts.join(" ") || "Disc Golf Disc";
	const condition = CONDITION_LABEL[listing.condition] ?? listing.condition;
	const discClass = listing.discClass
		? (DISC_CLASS_LABEL[listing.discClass] ?? listing.discClass)
		: null;
	const title = `${discName}${listing.weight ? ` ${listing.weight}g` : ""} — ${condition} | €${listing.price} · Re:Disc`;
	const descParts = [`${condition} ${discName}`];
	if (discClass) descParts.push(`(${discClass})`);
	if (listing.weight) descParts.push(`· ${listing.weight}g`);
	if (listing.plasticType) descParts.push(`· ${listing.plasticType}`);
	descParts.push(
		`for sale. €${listing.price}${listing.priceNegotiable ? " (negotiable)" : ""}.`,
	);
	if (listing.city && listing.country)
		descParts.push(`Located in ${listing.city}, ${listing.country}.`);
	return { title, description: descParts.join(" ") };
}

async function fetchListing(id: string) {
	const url = import.meta.env.VITE_CONVEX_URL;
	if (!url) return null;
	const convex = new ConvexHttpClient(url);
	return convex
		.query(api.listings.getById, { id: id as Id<"listings"> })
		.catch(() => null);
}

export const Route = createFileRoute("/listings/$id")({
	loader: async ({ params }) => fetchListing(params.id),
	head: ({ loaderData: listing }) => {
		const { title, description } = buildListingMeta(listing);
		const image = listing?.imageTopUrl ?? undefined;
		const schema =
			listing && listing.soldAt === undefined
				? JSON.stringify({
						"@context": "https://schema.org",
						"@type": "Product",
						name:
							[listing.manufacturerName, listing.discModelName]
								.filter(Boolean)
								.join(" ") || "Disc Golf Disc",
						...(listing.manufacturerName
							? { brand: { "@type": "Brand", name: listing.manufacturerName } }
							: {}),
						...(listing.description
							? { description: listing.description }
							: {}),
						...(image ? { image } : {}),
						offers: {
							"@type": "Offer",
							priceCurrency: "EUR",
							price: String(listing.price),
							availability: "https://schema.org/InStock",
							itemCondition: "https://schema.org/UsedCondition",
							...(listing.sellerUsername
								? {
										seller: { "@type": "Person", name: listing.sellerUsername },
									}
								: {}),
						},
					})
				: undefined;
		return {
			meta: [
				{ title },
				{ name: "description", content: description },
				{ property: "og:title", content: title },
				{ property: "og:description", content: description },
				...(image ? [{ property: "og:image", content: image }] : []),
				{ property: "og:type", content: "website" },
				{ name: "twitter:title", content: title },
				{ name: "twitter:description", content: description },
				...(image ? [{ name: "twitter:image", content: image }] : []),
			],
			links: image
				? [
						{
							rel: "preload",
							as: "image",
							href: image,
							fetchpriority: "high" as string,
						},
					]
				: [],
			...(schema
				? { scripts: [{ type: "application/ld+json", children: schema }] }
				: {}),
		};
	},
	component: ListingDetailPage,
});

function getDiscRimColor(colors: string[]): string {
	for (const c of colors) {
		if (COLOR_HEX[c]) return COLOR_HEX[c];
	}
	return "#718096";
}

function WatchlistButton({ listingId }: { listingId: Id<"listings"> }) {
	const { t } = useTranslation("listingDetail");
	const { isSignedIn } = useAuth();
	const { open: openSignIn } = useSignInModal();
	const { data: isSaved = false } = useQuery(
		convexQuery(api.watchlist.isInWatchlist, { listingId }),
	);
	const save = useMutation(api.watchlist.saveToWatchlist);
	const remove = useMutation(api.watchlist.removeFromWatchlist);

	function handleToggle() {
		if (!isSignedIn) {
			openSignIn();
			return;
		}
		if (isSaved) {
			remove({ listingId });
		} else {
			save({ listingId });
		}
	}

	return (
		<Button
			variant="outline"
			onClick={handleToggle}
			aria-label={
				isSaved
					? t("actions.removeFromWatchlist")
					: t("actions.saveToWatchlist")
			}
			className={cn(
				"gap-2 transition-all",
				isSaved &&
					"border-rose-500/40 bg-rose-500/10 text-rose-600 hover:border-rose-500/60 hover:bg-rose-500/15 dark:text-rose-400",
			)}
		>
			<Heart
				className={cn(
					"h-4 w-4 transition-colors",
					isSaved &&
						"fill-rose-500 text-rose-500 dark:fill-rose-400 dark:text-rose-400",
				)}
			/>
			{isSaved ? t("actions.saved") : t("actions.save")}
		</Button>
	);
}

function ListingDetailPage() {
	const { t } = useTranslation("listingDetail");
	const { t: tBrowse } = useTranslation("browse");
	const { id } = Route.useParams();
	const { userId } = useAuth();
	const discContainerRef = useRef<HTMLDivElement>(null);
	const [discSize, setDiscSize] = useState(LISTING_DISC_SIZE);
	const [actionPending, setActionPending] = useState(false);
	const incrementViewCount = useMutation(api.listings.incrementViewCount);
	const deactivate = useMutation(api.listings.deactivateListing);
	const markAsSold = useMutation(api.listings.markAsSold);
	const renew = useMutation(api.listings.renewListing);

	async function handleDeactivate() {
		setActionPending(true);
		try {
			await deactivate({ listingId: id as Doc<"listings">["_id"] });
		} finally {
			setActionPending(false);
		}
	}
	async function handleMarkAsSold() {
		setActionPending(true);
		try {
			await markAsSold({ listingId: id as Doc<"listings">["_id"] });
		} finally {
			setActionPending(false);
		}
	}
	async function handleReactivate() {
		setActionPending(true);
		try {
			await renew({ listingId: id as Doc<"listings">["_id"] });
		} finally {
			setActionPending(false);
		}
	}

	useEffect(() => {
		if (!id) return;
		try {
			const viewed = JSON.parse(
				localStorage.getItem("re-disc:viewed-listings") ?? "[]",
			) as string[];
			if (viewed.includes(id)) return;
			incrementViewCount({ listingId: id as Id<"listings"> });
			localStorage.setItem(
				"re-disc:viewed-listings",
				JSON.stringify([...viewed, id]),
			);
		} catch {
			// swallow parse errors
		}
	}, [id, incrementViewCount]);

	const { data: listing, isPending } = useQuery(
		convexQuery(api.listings.getById, { id: id as Id<"listings"> }),
	);
	const { canPrompt, showIOSTip, promptInstall, dismissInstall } =
		useInstallPrompt();
	const [showFallbackPrompt, setShowFallbackPrompt] = useState(false);

	useEffect(() => {
		incrementListingViewCount();
		if (hasReachedViewThreshold()) setShowFallbackPrompt(true);
	}, []);

	useEffect(() => {
		if (isPending) return;
		const el = discContainerRef.current;
		if (!el) return;
		const ro = new ResizeObserver(([entry]) => {
			setDiscSize(Math.min(LISTING_DISC_SIZE, entry.contentRect.width));
		});
		ro.observe(el);
		return () => ro.disconnect();
	}, [isPending]);

	const { data: sellerRating } = useQuery(
		convexQuery(
			api.reviews.getSellerRating,
			listing && listing.soldAt === undefined
				? { sellerId: listing.sellerId }
				: "skip",
		),
	);

	if (isPending) {
		return (
			<div className="min-h-screen">
				{/* Hero skeleton */}
				<div className="mx-auto max-w-5xl px-6 py-16 lg:py-20">
					<div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
						{/* Stats column — left on desktop, below disc on mobile */}
						<div className="lg:order-first order-last flex flex-col gap-6">
							<Skeleton className="h-6 w-24 rounded-full" />
							<div className="flex flex-col gap-2">
								<Skeleton className="h-3 w-20" />
								<Skeleton className="h-12 w-56 rounded-xl" />
							</div>
							<Skeleton className="h-12 w-32 rounded-xl" />
							<Skeleton className="h-px w-20" />
							<Skeleton className="h-4 w-48 rounded-full" />
							<div className="flex flex-col gap-2">
								<Skeleton className="h-3 w-28 rounded-full" />
								<div className="grid grid-cols-4 gap-2">
									{["speed", "glide", "turn", "fade"].map((stat) => (
										<Skeleton key={stat} className="h-20 rounded-xl" />
									))}
								</div>
							</div>
							<div className="flex flex-wrap gap-2">
								<Skeleton className="h-7 w-20 rounded-full" />
								<Skeleton className="h-7 w-20 rounded-full" />
								<Skeleton className="h-6 w-24 rounded-full" />
							</div>
						</div>

						{/* Disc circle — right on desktop, top on mobile */}
						<div className="flex items-center justify-center order-first lg:order-last">
							<Skeleton
								className="rounded-full"
								style={{
									width: discSize || LISTING_DISC_SIZE,
									height: discSize || LISTING_DISC_SIZE,
								}}
							/>
						</div>
					</div>
				</div>

				{/* Bottom sections skeleton */}
				<div className="mx-auto max-w-5xl px-6 py-12 flex flex-col gap-10">
					<div className="flex flex-col gap-3">
						<Skeleton className="h-3 w-20 rounded-full" />
						<div className="flex flex-col gap-2">
							<Skeleton className="h-4 w-full rounded-full" />
							<Skeleton className="h-4 w-5/6 rounded-full" />
							<Skeleton className="h-4 w-4/6 rounded-full" />
						</div>
					</div>
					<div className="flex flex-col gap-3">
						<Skeleton className="h-3 w-14 rounded-full" />
						<div className="flex gap-3">
							{["top", "bottom", "side"].map((angle) => (
								<Skeleton
									key={angle}
									className="shrink-0 rounded-xl"
									style={{ width: 112, height: 112 }}
								/>
							))}
						</div>
					</div>
					<div className="flex flex-col gap-1">
						<Skeleton className="h-3 w-16 rounded-full" />
						<Skeleton className="h-5 w-36 rounded-full" />
					</div>
					<div className="rounded-2xl border border-border p-6">
						<div className="flex items-start justify-between gap-4">
							<div className="flex flex-col gap-2">
								<Skeleton className="h-3 w-24 rounded-full" />
								<Skeleton className="h-6 w-40 rounded-full" />
							</div>
							<Skeleton className="h-8 w-16 rounded-lg" />
						</div>
					</div>
				</div>
			</div>
		);
	}

	if (!listing) {
		return <ListingNotFoundPage />;
	}

	if (listing.soldAt !== undefined) {
		return (
			<SoldListingPage
				discModelName={listing.discModelName}
				manufacturerName={listing.manufacturerName}
				listingId={id as Id<"listings">}
				sellerUsername={listing.sellerUsername ?? undefined}
			/>
		);
	}

	const isSeller = !!userId && listing.sellerId === userId;
	const rimColor = getDiscRimColor(listing.colors);

	const allImages = [
		{ url: listing.imageTopUrl, label: t("images.topView") },
		{ url: listing.imageBottomUrl, label: t("images.bottomView") },
		{ url: listing.imageSideUrl, label: t("images.sideView") },
		{ url: listing.imageExtraUrl, label: t("images.extra") },
	].filter((s): s is { url: string; label: string } => !!s.url);

	const displaySpeed = listing.officialSpeed ?? listing.estimatedSpeed;
	const displayGlide = listing.officialGlide ?? listing.estimatedGlide;
	const displayTurn = listing.officialTurn ?? listing.estimatedTurn;
	const displayFade = listing.officialFade ?? listing.estimatedFade;
	const hasFlightNumbers =
		displaySpeed != null ||
		displayGlide != null ||
		displayTurn != null ||
		displayFade != null;

	const showBanner = showFallbackPrompt && canPrompt;
	const showTip = showFallbackPrompt && showIOSTip;

	return (
		<div className="min-h-screen overflow-x-hidden">
			{showBanner && (
				<>
					<InstallBanner onInstall={promptInstall} onDismiss={dismissInstall} />
					<InstallBannerDesktop
						onInstall={promptInstall}
						onDismiss={dismissInstall}
					/>
				</>
			)}
			<IOSTipSheet open={showTip} onDismiss={dismissInstall} />
			{/* Back navigation */}
			<div className="mx-auto max-w-5xl px-6 pt-6 pb-2">
				<Link
					to="/listings"
					search={{
						sort: "newest",
						q: "",
						discClass: [],
						condition: [],
						color: [],
						shipsTo: [],
						country: [],
						plasticType: [],
						priceMin: "",
						priceMax: "",
						weightMin: "",
						weightMax: "",
						discModelIds: [],
						manufacturerIds: [],
						speedMin: "",
						speedMax: "",
						glideMin: "",
						glideMax: "",
						turnMin: "",
						turnMax: "",
						fadeMin: "",
						fadeMax: "",
						distanceKm: "",
					}}
					className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors group"
				>
					<ChevronLeft className="h-4 w-4 transition-transform group-hover:-translate-x-0.5" />
					{t("backToListings")}
				</Link>
			</div>

			{/* Hero */}
			<div
				style={{
					background: `radial-gradient(ellipse at 72% 55%, ${rimColor}28, transparent 60%)`,
				}}
			>
				<div className="mx-auto max-w-5xl px-6 py-12 lg:py-16">
					<div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
						{/* Stats — left on desktop, below disc on mobile */}
						<div className="lg:order-first order-last flex flex-col gap-6">
							{/* Disc class badge */}
							<div className="rise-in" style={{ animationDelay: "0ms" }}>
								{listing.discClass ? (
									<Badge
										variant="outline"
										className="uppercase text-xs tracking-widest font-semibold"
									>
										{t(`discClass.${listing.discClass}` as "discClass.putter")}
									</Badge>
								) : (
									<div className="h-6" />
								)}
							</div>

							{/* Manufacturer + model */}
							<div
								className="rise-in flex flex-col gap-1"
								style={{ animationDelay: "60ms" }}
							>
								{listing.manufacturerName && (
									<p className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">
										{listing.manufacturerName}
									</p>
								)}
								<h1 className="display-title text-[clamp(48px,6vw,72px)] leading-none text-foreground">
									{listing.discModelName ?? "Disc"}
								</h1>
							</div>

							{/* Price */}
							<div
								className="rise-in flex items-baseline gap-2"
								style={{ animationDelay: "120ms" }}
							>
								<span className="display-title text-[56px] leading-none text-primary">
									€{listing.price}
								</span>
								{listing.priceNegotiable && (
									<Badge variant="outline" className="text-xs">
										{t("price.negotiable")}
									</Badge>
								)}
								{isSeller && !listing.isActive && (
									<Badge variant="secondary" className="text-xs">
										{t("price.inactive")}
									</Badge>
								)}
							</div>

							{/* Action buttons — desktop only; mobile uses sticky bar */}
							<div
								className="rise-in hidden lg:flex flex-wrap gap-2"
								style={{ animationDelay: "150ms" }}
							>
								{isSeller ? (
									<>
										<Button variant="outline" asChild>
											<Link to="/edit/$id" params={{ id }}>
												<Pencil className="h-4 w-4" />
												{t("actions.edit")}
											</Link>
										</Button>
										{listing.isActive ? (
											<>
												<Button
													variant="outline"
													disabled={actionPending}
													onClick={handleMarkAsSold}
												>
													{t("actions.markAsSold")}
												</Button>
												<Button
													variant="ghost"
													disabled={actionPending}
													onClick={handleDeactivate}
												>
													{t("actions.deactivate")}
												</Button>
											</>
										) : (
											<Button
												disabled={actionPending}
												onClick={handleReactivate}
											>
												{t("actions.reactivate")}
											</Button>
										)}
									</>
								) : (
									<>
										<WatchlistButton listingId={id as Id<"listings">} />
										<ContactSellerPanel
											listingId={id as Id<"listings">}
											sellerId={listing.sellerId}
											discModelName={listing.discModelName}
											priceNegotiable={listing.priceNegotiable}
										/>
									</>
								)}
							</div>

							{/* Gradient separator */}
							<div className="rise-in" style={{ animationDelay: "160ms" }}>
								<div
									className="h-px w-20"
									style={{
										background: `linear-gradient(to right, ${rimColor}99, transparent)`,
									}}
								/>
							</div>

							{/* Key specs as pills */}
							<div
								className="rise-in flex flex-wrap gap-2"
								style={{ animationDelay: "200ms" }}
							>
								{listing.plasticType && (
									<span className="inline-flex items-center rounded-full border border-border px-2.5 py-1 text-xs font-semibold text-foreground">
										{listing.plasticType}
									</span>
								)}
								{listing.weight && (
									<span className="inline-flex items-center rounded-full border border-border px-2.5 py-1 text-xs font-medium text-muted-foreground">
										{listing.weight}g
									</span>
								)}
								<span className="inline-flex items-center rounded-full border border-border px-2.5 py-1 text-xs font-medium text-muted-foreground">
									{t(`condition.${listing.condition}` as "condition.9")}
								</span>
							</div>

							{/* Flight numbers */}
							{hasFlightNumbers && (
								<div className="rise-in" style={{ animationDelay: "260ms" }}>
									<FlightNumbersGrid
										speed={displaySpeed}
										glide={displayGlide}
										turn={displayTurn}
										fade={displayFade}
										sectionLabel={t("flightNumbers.label")}
										labels={{
											speed: t("flightNumbers.speed"),
											glide: t("flightNumbers.glide"),
											turn: t("flightNumbers.turn"),
											fade: t("flightNumbers.fade"),
										}}
										accentColor={rimColor}
									/>
								</div>
							)}

							{/* Colors + shipping */}
							<div
								className="rise-in flex flex-wrap gap-2 items-center"
								style={{ animationDelay: "320ms" }}
							>
								{listing.colors.map((color) => (
									<div
										key={color}
										className="flex items-center gap-1.5 rounded-full border border-border px-2.5 py-1 text-xs font-medium text-foreground"
									>
										<div
											className="w-3 h-3 rounded-full shrink-0"
											style={
												color === "Dyed"
													? { background: DYED_GRADIENT }
													: color === "Transparent"
														? { background: TRANSPARENT_PATTERN }
														: { background: COLOR_HEX[color] ?? "#718096" }
											}
										/>
										{tBrowse(`colors.${color}` as "colors.Red")}
									</div>
								))}
								<Badge variant="secondary">
									{t(`shipsTo.${listing.shipsTo}` as "shipsTo.domestic")}
								</Badge>
							</div>
						</div>

						{/* 3D Disc — right on desktop, top on mobile */}
						<div
							ref={discContainerRef}
							className="rise-in flex items-center justify-center order-first lg:order-last w-full"
							style={{
								animationDelay: "40ms",
								viewTransitionName: `disc-${id}`,
							}}
						>
							{discSize > 0 &&
								(listing.imageTopUrl ? (
									<Disc3D
										topUrl={listing.imageTopUrl}
										bottomUrl={listing.imageBottomUrl}
										rimColor={rimColor}
										discClass={listing.discClass}
										size={discSize}
										priority={true}
									/>
								) : (
									<div
										className="rounded-full bg-muted flex items-center justify-center text-muted-foreground text-sm"
										style={{
											width: discSize,
											height: discSize,
										}}
									>
										{t("images.noImage")}
									</div>
								))}
						</div>
					</div>
				</div>
			</div>

			{/* Bottom sections */}
			<div className="mx-auto max-w-5xl px-6 pb-[calc(6rem+env(safe-area-inset-bottom))] lg:pb-16 divide-y divide-border">
				{listing.description && (
					<section className="py-10 flex flex-col gap-3">
						<h2 className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">
							{t("sections.description")}
						</h2>
						<p className="text-base text-foreground leading-relaxed whitespace-pre-wrap">
							{listing.description}
						</p>
					</section>
				)}

				{allImages.length > 0 && (
					<section className="py-10 flex flex-col gap-3">
						<h2 className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">
							{t("sections.photos")}
						</h2>
						<ImageGallery images={allImages} />
					</section>
				)}

				<section className="py-10 flex flex-col gap-1.5">
					<h2 className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">
						{t("sections.location")}
					</h2>
					<div className="flex items-center gap-1.5 text-base text-foreground">
						<MapPin className="h-4 w-4 shrink-0 text-muted-foreground" />
						{listing.city}, {listing.country}
					</div>
				</section>

				{listing.sellerUsername && (
					<section className="py-10">
						<Link
							to="/sellers/$username"
							params={{ username: listing.sellerUsername }}
							className="group flex items-center gap-4 rounded-[var(--radius)] border border-border p-4 hover:border-foreground/20 transition-colors"
						>
							<div className="h-10 w-10 rounded-full bg-muted flex items-center justify-center shrink-0 text-muted-foreground">
								<User className="h-5 w-5" />
							</div>
							<div className="flex flex-col gap-0.5 min-w-0">
								<p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">
									{t("sections.seller")}
								</p>
								<p className="text-sm font-semibold text-foreground">
									@{listing.sellerUsername}
								</p>
								{sellerRating && sellerRating.count > 0 && (
									<span className="text-sm text-muted-foreground">
										★ {sellerRating.averageRating?.toFixed(1)} ·{" "}
										{t("reviews", { count: sellerRating.count })}
									</span>
								)}
							</div>
							<span className="ml-auto text-sm text-muted-foreground group-hover:text-foreground transition-colors shrink-0">
								{t("sections.viewProfile")}
							</span>
						</Link>
					</section>
				)}

				<section className="py-6 flex justify-end">
					<ReportListingButton
						listingId={id as Id<"listings">}
						sellerId={listing.sellerId}
					/>
				</section>
			</div>

			{/* Mobile sticky action bar */}
			<div className="lg:hidden fixed bottom-0 inset-x-0 z-40 border-t border-border bg-background/95 backdrop-blur-sm px-4 pt-3 pb-[calc(0.75rem+env(safe-area-inset-bottom))] flex gap-2">
				{isSeller ? (
					<>
						<Button variant="outline" asChild>
							<Link to="/edit/$id" params={{ id }}>
								<Pencil className="h-4 w-4" />
								{t("actions.edit")}
							</Link>
						</Button>
						{listing.isActive ? (
							<>
								<Button
									variant="outline"
									className="flex-1"
									disabled={actionPending}
									onClick={handleMarkAsSold}
								>
									{t("actions.markAsSold")}
								</Button>
								<Button
									variant="ghost"
									className="flex-1"
									disabled={actionPending}
									onClick={handleDeactivate}
								>
									{t("actions.deactivate")}
								</Button>
							</>
						) : (
							<Button
								className="flex-1"
								disabled={actionPending}
								onClick={handleReactivate}
							>
								{t("actions.reactivate")}
							</Button>
						)}
					</>
				) : (
					<>
						<WatchlistButton listingId={id as Id<"listings">} />
						<ContactSellerPanel
							listingId={id as Id<"listings">}
							sellerId={listing.sellerId}
							discModelName={listing.discModelName}
							priceNegotiable={listing.priceNegotiable}
						/>
					</>
				)}
			</div>
		</div>
	);
}
